import {
  createFirstTimeProduct,
  trackedProducts,
  type Product,
} from "../mocks/products";

const SHOPEE_HOSTS = new Set([
  "shopee.vn",
  "www.shopee.vn",
]);

const identityPatterns = [
  /\/product\/(?<shopId>\d+)\/(?<itemId>\d+)/i,
  /-i\.(?<shopId>\d+)\.(?<itemId>\d+)$/i,
  /^\/(?<shopId>\d+)\/(?<itemId>\d+)$/i,
];

export type SearchResolution =
  | {
      type: "tracked";
      product: Product;
      message: string;
    }
  | {
      type: "new";
      product: Product;
      message: string;
    }
  | {
      type: "local-match";
      product: Product;
      message: string;
    }
  | {
      type: "invalid";
      product: Product;
      message: string;
    };

const fallbackProduct = trackedProducts[0];

const parseShopeeIdentity = (
  query: string,
): { productKey: string; canonicalUrl: string } | null => {
  let url: URL;
  try {
    url = new URL(query.trim());
  } catch {
    return null;
  }

  if (!SHOPEE_HOSTS.has(url.hostname.toLowerCase())) {
    return null;
  }

  const pathname = url.pathname.replace(/\/+$/, "");
  for (const pattern of identityPatterns) {
    const match = pathname.match(pattern);
    const shopId = match?.groups?.shopId;
    const itemId = match?.groups?.itemId;
    if (shopId && itemId) {
      return {
        productKey: `VN:${shopId}:${itemId}`,
        canonicalUrl: `https://shopee.vn/product/${shopId}/${itemId}`,
      };
    }
  }

  return null;
};

export const resolveProductSearch = (query: string): SearchResolution => {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length === 0) {
    return {
      type: "invalid",
      product: fallbackProduct,
      message: "Enter a Shopee URL or tracked listing name.",
    };
  }

  const parsedIdentity = parseShopeeIdentity(trimmedQuery);
  if (parsedIdentity) {
    const existingProduct = trackedProducts.find(
      (product) => product.productKey === parsedIdentity.productKey,
    );
    if (existingProduct) {
      return {
        type: "tracked",
        product: existingProduct,
        message: "Interest recorded. Latest cached history is shown.",
      };
    }

    return {
      type: "new",
      product: createFirstTimeProduct(
        parsedIdentity.productKey,
        parsedIdentity.canonicalUrl,
      ),
      message: "Interest recorded. Tracking starts now.",
    };
  }

  const normalizedQuery = trimmedQuery.toLowerCase();
  const localMatch = trackedProducts.find((product) =>
    `${product.productName} ${product.shopName}`
      .toLowerCase()
      .includes(normalizedQuery),
  );

  if (localMatch) {
    return {
      type: "local-match",
      product: localMatch,
      message: "Matched an existing tracked listing.",
    };
  }

  return {
    type: "invalid",
    product: fallbackProduct,
    message: "MVP search only supports tracked listings or Shopee product URLs.",
  };
};
